import { NavLink } from 'react-router-dom'
import { useReviewSummary } from '@/api/queries'
import { CountBadge } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'

interface Item {
  label: string
  to: string
  /** Which review-summary figure feeds the badge, if any. */
  badge?: 'total' | 'programs' | 'quotes' | 'media' | 'facts'
  phase2?: boolean
}

/**
 * Grouped: ingestion · review · output. Seven flat items read as an
 * undifferentiated list (docs/02-app-shell.md §2).
 */
const GROUPS: Array<{ heading: string; items: Item[] }> = [
  {
    heading: 'Ingestion',
    items: [
      { label: 'Client profile', to: 'profile' },
      { label: 'Crawl dashboard', to: 'crawl', badge: 'total' },
    ],
  },
  {
    heading: 'Review',
    items: [
      { label: 'Programs & segments', to: 'programs', badge: 'programs' },
      { label: 'Quotes', to: 'quotes', badge: 'quotes' },
      { label: 'Media library', to: 'media', badge: 'media' },
      { label: 'Partner facts', to: 'facts', badge: 'facts' },
    ],
  },
  {
    heading: 'Output',
    items: [
      { label: 'Copy library', to: 'copy' },
      { label: 'Brand profile', to: 'brand', phase2: true },
    ],
  },
]

/**
 * With no client selected the rail renders disabled rather than hidden, so the
 * shape of the app stays legible from the client list.
 *
 * Badge counts come from one review-summary call, never one per item.
 */
export function NavRail({ clientId }: { clientId: string | null }) {
  const { data: summary } = useReviewSummary(clientId)

  const counts = {
    programs: summary?.programs ?? 0,
    quotes: summary?.quotes ?? 0,
    media: summary?.media ?? 0,
    facts: summary?.facts ?? 0,
    total: summary
      ? summary.programs + summary.quotes + summary.media + summary.facts
      : 0,
  }

  return (
    <nav
      aria-label="Client sections"
      className="w-[224px] shrink-0 border-r border-rule bg-surface-sunk"
    >
      <div className="sticky top-[58px] space-y-4 py-4">
        {GROUPS.map((group) => (
          <div key={group.heading}>
            <p className="field-label px-4 pb-1.5 uppercase tracking-[0.04em] text-ink-faint">
              {group.heading}
            </p>
            <ul>
              {group.items.map((item) => (
                <li key={item.to}>
                  {clientId ? (
                    <NavLink
                      to={`/c/${clientId}/${item.to}`}
                      className={({ isActive }) =>
                        cn(
                          'relative flex items-center gap-2 py-1.5 pl-4 pr-3 transition-colors',
                          isActive
                            ? 'bg-surface text-ink before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-brand-red'
                            : 'text-ink-muted hover:bg-surface hover:text-ink',
                        )
                      }
                    >
                      <span className="nav-label truncate">{item.label}</span>
                      {item.phase2 && (
                        <span className="ml-auto text-[10px] uppercase text-ink-faint">Soon</span>
                      )}
                      {item.badge && (
                        <CountBadge count={counts[item.badge]} className="ml-auto" />
                      )}
                    </NavLink>
                  ) : (
                    <span
                      aria-disabled
                      className="flex cursor-not-allowed items-center py-1.5 pl-4 pr-3 opacity-40"
                    >
                      <span className="nav-label truncate">{item.label}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  )
}
